import * as React from "react";
import {
  AiPaymentService,
  type AIWorkspaceAnalyticsResponse,
  type AIWorkspaceModelBreakdownRow,
  type AIWorkspaceProjectBreakdownRow,
  type AIWorkspaceRange,
  type AIWorkspaceRequestRow,
  type AIWorkspaceRequestsResponse,
  type AIWorkspaceTrendPoint,
  type AIWorkspaceUserBreakdownRow
} from "../../../../../services/ai-payment-service";
import KuikaButton from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaEmptyState from "../../../../ui-kit/kuika-empty-state/kuika-empty-state";
import KuikaNumber from "../../../../ui-kit/kuika-number/kuika-number";
import {
  KuikaSkeletonRegion,
  KuikaSkeletonStats,
  KuikaSkeletonTable
} from "../../../../ui-kit/kuika-skeleton/kuika-skeleton";
import KuikaSvg from "../../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTabContent from "../../../../ui-kit/kuika-tab-content/kuika-tab-content";
import KuikaTable, { type KuikaTableColumn } from "../../../../ui-kit/kuika-table/kuika-table";
import KuikaTabs from "../../../../ui-kit/kuika-tabs/kuika-tabs";
import { extractBackendError, formatCreditAmount } from "../ai-chatbot-utils";
import type { TrendChartPoint } from "../charts/chart-types";
import { LazyModelMixChart, LazyTrendChart } from "../charts/lazy";
import AnalyticsRequestDrawer from "./analytics/analytics-request-drawer";
import "./analytics-tab.scss";

// ── Analytics tab (KOA-18066) ───────────────────────────────────────────────
// Owner-only usage analytics: KPI tiles, credits trend (day/week/month
// roll-up computed client-side from the server's daily series), member /
// project / model breakdowns as ranked bar lists, and the request log with a
// detail drawer. Request LIST is Backend-durable; per-request CONTENT comes
// from the chat server (see analytics-request-drawer).

type Granularity = "day" | "week" | "month";
type BreakdownKey = "members" | "projects" | "models";

const RANGE_OPTIONS: { value: AIWorkspaceRange; label: string }[] = [
  { value: "7d", label: "7d" },
  { value: "30d", label: "30d" },
  { value: "90d", label: "90d" },
  { value: "mtd", label: "This month" }
];

const GRANULARITY_OPTIONS: { value: Granularity; label: string }[] = [
  { value: "day", label: "Day" },
  { value: "week", label: "Week" },
  { value: "month", label: "Month" }
];

const BREAKDOWN_OPTIONS: { value: BreakdownKey; label: string }[] = [
  { value: "members", label: "Members" },
  { value: "projects", label: "Projects" },
  { value: "models", label: "Models" }
];

/** The breakdown tabs in their on-screen order, so the ranked list slides the way the selection moved. */
const BREAKDOWN_ORDER: string[] = BREAKDOWN_OPTIONS.map((option): string => option.value);

/** One KPI tile: its number rolls when the range changes, and reads "—" before the first answer. */
interface IKpiTile {
  label: string;
  value: number | null;
  format: (value: number) => string;
  sub?: string;
}

const formatCount = (count: number): string => count.toLocaleString("en-US");

const TOP_BREAKDOWN_COUNT = 8;
const REQUEST_FETCH_SIZE = 500;

interface State {
  range: AIWorkspaceRange;
  granularity: Granularity;
  analytics: AIWorkspaceAnalyticsResponse | null;
  analyticsLoading: boolean;
  analyticsError: string | null;
  requests: AIWorkspaceRequestRow[];
  requestsLoading: boolean;
  breakdownTab: BreakdownKey;
  showAllBreakdown: boolean;
  selectedRequest: AIWorkspaceRequestRow | null;
}

/** "2026-07-21" (UTC day) → short label like "Jul 21". */
function dayLabel(date: string): string {
  const parsed: Date = new Date(`${date}T00:00:00Z`);
  return parsed.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
}

/** Monday (UTC) of the ISO week containing the given UTC day. */
function isoWeekStart(date: string): string {
  const parsed: Date = new Date(`${date}T00:00:00Z`);
  const daysSinceMonday: number = (parsed.getUTCDay() + 6) % 7;
  parsed.setUTCDate(parsed.getUTCDate() - daysSinceMonday);
  return parsed.toISOString().slice(0, 10);
}

/** Roll the daily series up to the requested granularity, preserving order. */
function rollUpTrend(daily: AIWorkspaceTrendPoint[], granularity: Granularity): TrendChartPoint[] {
  if (granularity === "day") {
    return daily.map((p: AIWorkspaceTrendPoint): TrendChartPoint => ({
      label: dayLabel(p.date),
      value: p.creditsConsumed,
      requestCount: p.requestCount
    }));
  }

  const buckets: Map<string, { value: number; requestCount: number }> = new Map();
  for (const point of daily) {
    const key: string = granularity === "week" ? isoWeekStart(point.date) : point.date.slice(0, 7);
    const bucket = buckets.get(key) ?? { value: 0, requestCount: 0 };
    bucket.value += point.creditsConsumed;
    bucket.requestCount += point.requestCount;
    buckets.set(key, bucket);
  }
  return Array.from(buckets.entries()).map(([key, bucket]): TrendChartPoint => {
    if (granularity === "week") {
      return { label: `Wk of ${dayLabel(key)}`, value: bucket.value, requestCount: bucket.requestCount };
    }
    const monthDate: Date = new Date(`${key}-01T00:00:00Z`);
    const label: string = monthDate.toLocaleDateString("en-US", { month: "short", year: "numeric", timeZone: "UTC" });
    return { label, value: bucket.value, requestCount: bucket.requestCount };
  });
}

function compactTokens(count: number): string {
  if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1)}M`;
  if (count >= 1_000) return `${(count / 1_000).toFixed(1)}K`;
  return count.toLocaleString("en-US");
}

class AnalyticsTab extends React.PureComponent<{}, State> {
  state: State = {
    range: "30d",
    granularity: "day",
    analytics: null,
    analyticsLoading: true,
    analyticsError: null,
    requests: [],
    requestsLoading: true,
    breakdownTab: "members",
    showAllBreakdown: false,
    selectedRequest: null
  };

  private loadControllers: Map<string, AbortController> = new Map();

  private isUnmounted = false;

  async componentDidMount() {
    await this.loadAll(this.state.range);
  }

  componentWillUnmount() {
    this.isUnmounted = true;
    for (const controller of this.loadControllers.values()) controller.abort();
  }

  private safeSetState = (patch: Partial<State>) => {
    if (this.isUnmounted) return;
    this.setState(patch as State);
  };

  private nextSignal(key: string): AbortSignal {
    this.loadControllers.get(key)?.abort();
    const controller: AbortController = new AbortController();
    this.loadControllers.set(key, controller);
    return controller.signal;
  }

  private loadAll = async (range: AIWorkspaceRange): Promise<void> => {
    await Promise.all([this.loadAnalytics(range), this.loadRequests(range)]);
  };

  private loadAnalytics = async (range: AIWorkspaceRange): Promise<void> => {
    const signal: AbortSignal = this.nextSignal("analytics");
    this.safeSetState({ analyticsLoading: true, analyticsError: null });
    try {
      const analytics: AIWorkspaceAnalyticsResponse = await AiPaymentService.GetWorkspaceAnalytics({ range }, signal);
      if (signal.aborted) return;
      this.safeSetState({ analytics, analyticsLoading: false });
    } catch (err: unknown) {
      if (signal.aborted) return;
      this.safeSetState({ analyticsLoading: false, analyticsError: extractBackendError(err) });
    }
  };

  private loadRequests = async (range: AIWorkspaceRange): Promise<void> => {
    const signal: AbortSignal = this.nextSignal("requests");
    this.safeSetState({ requestsLoading: true });
    try {
      // One shot, client-mode table (admin-panel precedent): sort/filter/search
      // run locally over the newest REQUEST_FETCH_SIZE rows.
      const res: AIWorkspaceRequestsResponse = await AiPaymentService.GetWorkspaceRequests(
        { range, page: 1, pageSize: REQUEST_FETCH_SIZE, sortBy: "createdAt", sortDirection: "desc" },
        signal
      );
      if (signal.aborted) return;
      this.safeSetState({ requests: res.rows, requestsLoading: false });
    } catch {
      if (signal.aborted) return;
      // Analytics card already surfaces the load error; the log just empties.
      this.safeSetState({ requestsLoading: false });
    }
  };

  private handleRangeChange = (value: string) => {
    const range: AIWorkspaceRange = (RANGE_OPTIONS.find((o) => o.value === value)?.value ?? "30d") as AIWorkspaceRange;
    this.setState({ range });
    void this.loadAll(range);
  };

  // ── KPI hero band ────────────────────────────────────────────────

  private windowLabel(): string {
    const { analytics } = this.state;
    if (!analytics?.windowFrom || !analytics?.windowTo) return "";
    const fmt = (iso: string): string => new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
    return `${fmt(analytics.windowFrom)} – ${fmt(analytics.windowTo)}`;
  }

  private renderKpis() {
    const kpis = this.state.analytics?.kpis ?? null;
    const tiles: IKpiTile[] = [
      { label: "Credits used", value: kpis ? kpis.creditsConsumed : null, format: formatCreditAmount },
      { label: "Chats", value: kpis ? kpis.requestCount : null, format: formatCount },
      {
        label: "Tokens",
        value: kpis ? kpis.tokensInput + kpis.tokensOutput : null,
        format: compactTokens,
        sub: kpis ? `↑ ${compactTokens(kpis.tokensInput)} in · ↓ ${compactTokens(kpis.tokensOutput)} out` : undefined
      },
      { label: "Active members", value: kpis ? kpis.activeUsers : null, format: formatCount }
    ];
    const windowLabel: string = this.windowLabel();
    return (
      <div className="aichat-analytics__hero">
        <div className="aichat-analytics__hero-top">
          <span className="aichat-analytics__pill">Usage analytics</span>
          {windowLabel && <span className="aichat-analytics__hero-window">{windowLabel}</span>}
        </div>
        <div className="aichat-analytics__hero-stats">
          {tiles.map((tile) => (
            <div key={tile.label} className="aichat-analytics__hero-stat">
              <span className="aichat-analytics__hero-stat-label">{tile.label}</span>
              <span className="aichat-analytics__hero-stat-value">
                {tile.value === null ? "—" : <KuikaNumber value={tile.value} format={tile.format} />}
              </span>
              {tile.sub && <span className="aichat-analytics__hero-stat-sub">{tile.sub}</span>}
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ── Trend card ───────────────────────────────────────────────────

  private renderTrendCard() {
    const { analytics, granularity } = this.state;
    const daily: AIWorkspaceTrendPoint[] = analytics?.dailyTrend ?? [];
    const points: TrendChartPoint[] = rollUpTrend(daily, granularity);
    const hasData: boolean = points.some((p: TrendChartPoint): boolean => p.value > 0 || p.requestCount > 0);

    return (
      <div className="aichat-analytics__card">
        <div className="aichat-analytics__card-head">
          <span className="aichat-analytics__card-title">Credits used</span>
          <KuikaTabs
            size="sm"
            options={GRANULARITY_OPTIONS}
            value={granularity}
            onChange={(value: string) =>
              this.setState({ granularity: value === "week" || value === "month" ? value : "day" })
            }
          />
        </div>
        {hasData ? (
          <LazyTrendChart points={points} formatValue={formatCreditAmount} valueLabel="credits" />
        ) : (
          <KuikaEmptyState
            svgName="info-circle"
            customName="No AI activity in this period"
            description="Usage will appear here as your team chats with the AI."
          />
        )}
      </div>
    );
  }

  // ── Breakdown card ───────────────────────────────────────────────

  private renderBarRow(
    key: string,
    rank: number,
    icon: React.ReactNode,
    name: string,
    credits: number,
    chats: number,
    max: number
  ) {
    const percent: number = max > 0 ? Math.max(2, Math.round((credits / max) * 100)) : 0;
    const rankLabel: string = String(rank).padStart(2, "0");
    return (
      <div key={key} className="aichat-analytics__barlist-row">
        <span className="aichat-analytics__barlist-fill" style={{ width: `${percent}%` }} aria-hidden="true" />
        <div className="aichat-analytics__barlist-content">
          <span className="aichat-analytics__barlist-rank">{rankLabel}</span>
          <span className="aichat-analytics__barlist-icon">{icon}</span>
          <span className="aichat-analytics__barlist-name">{name}</span>
          <span className="aichat-analytics__barlist-value">
            <strong>{formatCreditAmount(credits)}</strong> credits · {chats.toLocaleString("en-US")} chats
          </span>
        </div>
      </div>
    );
  }

  private memberInitials(row: AIWorkspaceUserBreakdownRow): string {
    const first: string = row.firstName?.trim()?.charAt(0) ?? "";
    const last: string = row.lastName?.trim()?.charAt(0) ?? "";
    const fallback: string = row.email?.trim()?.charAt(0) ?? "?";
    return (`${first}${last}` || fallback).toUpperCase();
  }

  private renderBreakdownRows(breakdownTab: BreakdownKey): React.ReactNode[] {
    const { analytics, showAllBreakdown } = this.state;
    if (!analytics) return [];

    if (breakdownTab === "members") {
      const rows: AIWorkspaceUserBreakdownRow[] = analytics.byUser;
      const max: number = rows[0]?.creditsConsumed ?? 0;
      const visible: AIWorkspaceUserBreakdownRow[] = showAllBreakdown ? rows : rows.slice(0, TOP_BREAKDOWN_COUNT);
      return visible.map((row: AIWorkspaceUserBreakdownRow, index: number) => {
        const name: string = [row.firstName, row.lastName].filter(Boolean).join(" ").trim() || (row.email ?? "Unknown");
        const icon: React.ReactNode = row.profilePhotoUrl ? (
          <img className="aichat-analytics__barlist-avatar" src={row.profilePhotoUrl} alt="" />
        ) : (
          <span className="aichat-analytics__barlist-avatar aichat-analytics__barlist-avatar--initials">
            {this.memberInitials(row)}
          </span>
        );
        return this.renderBarRow(row.userId ?? name, index + 1, icon, name, row.creditsConsumed, row.requestCount, max);
      });
    }

    if (breakdownTab === "projects") {
      const rows: AIWorkspaceProjectBreakdownRow[] = analytics.byProject;
      const max: number = rows[0]?.creditsConsumed ?? 0;
      const visible: AIWorkspaceProjectBreakdownRow[] = showAllBreakdown ? rows : rows.slice(0, TOP_BREAKDOWN_COUNT);
      return visible.map((row: AIWorkspaceProjectBreakdownRow, index: number) =>
        this.renderBarRow(
          row.projectId ?? "unattributed",
          index + 1,
          <KuikaSvg name="folder" type="sidebar-icons" width={16} height={16} />,
          row.projectName ?? "Unattributed",
          row.creditsConsumed,
          row.requestCount,
          max
        )
      );
    }

    const rows: AIWorkspaceModelBreakdownRow[] = analytics.byModel;
    const max: number = rows[0]?.creditsConsumed ?? 0;
    const visible: AIWorkspaceModelBreakdownRow[] = showAllBreakdown ? rows : rows.slice(0, TOP_BREAKDOWN_COUNT);
    return visible.map((row: AIWorkspaceModelBreakdownRow, index: number) =>
      this.renderBarRow(
        row.model ?? "unknown",
        index + 1,
        <KuikaSvg name="sparkles" type="datasource-icons" width={16} height={16} />,
        row.model ?? "Unknown model",
        row.creditsConsumed,
        row.requestCount,
        max
      )
    );
  }

  /**
   * One breakdown tab's pane. Rendered per key, so the pane that leaves on a tab change keeps
   * drawing its own ranking while the new one slides in.
   */
  private renderBreakdownPane = (paneKey: string): React.ReactNode => {
    const { analytics, showAllBreakdown } = this.state;
    const breakdownTab: BreakdownKey = paneKey === "projects" || paneKey === "models" ? paneKey : "members";
    let totalRows = 0;
    if (analytics) {
      if (breakdownTab === "members") totalRows = analytics.byUser.length;
      else if (breakdownTab === "projects") totalRows = analytics.byProject.length;
      else totalRows = analytics.byModel.length;
    }
    const rows: React.ReactNode[] = this.renderBreakdownRows(breakdownTab);

    const modelSlices =
      breakdownTab === "models" && analytics
        ? analytics.byModel.slice(0, 5).map((row: AIWorkspaceModelBreakdownRow) => ({
            name: row.model ?? "Unknown",
            value: row.creditsConsumed
          }))
        : [];
    const otherCredits: number =
      breakdownTab === "models" && analytics
        ? analytics.byModel.slice(5).reduce((sum: number, row: AIWorkspaceModelBreakdownRow): number => {
            return sum + row.creditsConsumed;
          }, 0)
        : 0;
    if (otherCredits > 0) modelSlices.push({ name: "Other", value: otherCredits });

    return (
      <>
        {rows.length === 0 ? (
          <KuikaEmptyState
            svgName="info-circle"
            customName="Nothing here yet"
            description="Breakdowns appear once there's AI activity."
          />
        ) : (
          <div className="aichat-analytics__breakdown">
            <div className="aichat-analytics__barlist">{rows}</div>
            {breakdownTab === "models" && modelSlices.length > 1 && (
              <div className="aichat-analytics__donut">
                <LazyModelMixChart slices={modelSlices} formatValue={formatCreditAmount} />
              </div>
            )}
          </div>
        )}
        {totalRows > TOP_BREAKDOWN_COUNT && (
          <button
            type="button"
            className="aichat-analytics__show-all"
            onClick={() => this.setState({ showAllBreakdown: !showAllBreakdown })}
          >
            {showAllBreakdown ? "Show top 8" : `Show all (${totalRows})`}
          </button>
        )}
      </>
    );
  };

  private renderBreakdownCard() {
    const { breakdownTab } = this.state;
    return (
      <div className="aichat-analytics__card">
        <div className="aichat-analytics__card-head">
          <span className="aichat-analytics__card-title">Where credits go</span>
          <KuikaTabs
            size="sm"
            options={BREAKDOWN_OPTIONS}
            value={breakdownTab}
            onChange={(value: string) =>
              this.setState({
                breakdownTab: value === "projects" || value === "models" ? value : "members",
                showAllBreakdown: false
              })
            }
          />
        </div>
        <KuikaTabContent activeKey={breakdownTab} order={BREAKDOWN_ORDER} render={this.renderBreakdownPane} />
      </div>
    );
  }

  // ── Requests card ────────────────────────────────────────────────

  private buildRequestColumns(): KuikaTableColumn<AIWorkspaceRequestRow>[] {
    return [
      {
        id: "when",
        header: () => "When",
        cell: (row: AIWorkspaceRequestRow) =>
          new Date(row.createdAt).toLocaleString("en-US", {
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit"
          }),
        accessorFn: (row: AIWorkspaceRequestRow): string => row.createdAt,
        enableSorting: true,
        width: 150
      },
      {
        id: "member",
        header: () => "Member",
        cell: (row: AIWorkspaceRequestRow) => row.userEmail ?? "—",
        accessorFn: (row: AIWorkspaceRequestRow): string => row.userEmail ?? "",
        enableSorting: true,
        enableColumnFilter: true,
        filterVariant: "multiselect",
        width: 200
      },
      {
        id: "project",
        header: () => "Project",
        cell: (row: AIWorkspaceRequestRow) => row.projectName ?? "Unattributed",
        accessorFn: (row: AIWorkspaceRequestRow): string => row.projectName ?? "Unattributed",
        enableSorting: true,
        enableColumnFilter: true,
        filterVariant: "multiselect",
        width: 170
      },
      {
        id: "model",
        header: () => "Model",
        cell: (row: AIWorkspaceRequestRow) => row.model ?? "—",
        accessorFn: (row: AIWorkspaceRequestRow): string => row.model ?? "",
        enableSorting: true,
        enableColumnFilter: true,
        filterVariant: "multiselect",
        width: 170
      },
      {
        id: "credits",
        header: () => "Credits",
        cell: (row: AIWorkspaceRequestRow) => formatCreditAmount(row.creditsConsumed),
        accessorFn: (row: AIWorkspaceRequestRow): number => row.creditsConsumed,
        enableSorting: true,
        align: "right",
        width: 100
      },
      {
        id: "tokens",
        header: () => "Tokens",
        cell: (row: AIWorkspaceRequestRow) => compactTokens(row.tokensInput + row.tokensOutput),
        accessorFn: (row: AIWorkspaceRequestRow): number => row.tokensInput + row.tokensOutput,
        enableSorting: true,
        align: "right",
        width: 100
      }
    ];
  }

  private renderRequestsCard() {
    const { requests, requestsLoading } = this.state;
    return (
      <div className="aichat-analytics__card">
        <div className="aichat-analytics__card-head">
          <span className="aichat-analytics__card-title">Recent requests</span>
        </div>
        <KuikaTable<AIWorkspaceRequestRow>
          data={requests}
          columns={this.buildRequestColumns()}
          rowKey={(row: AIWorkspaceRequestRow): string => row.id}
          pageSize={25}
          enableSorting
          enableColumnFilters
          enableGlobalFilter
          loading={requestsLoading}
          onRowClick={(row: AIWorkspaceRequestRow) => this.setState({ selectedRequest: row })}
          emptyTitle="No requests in this period"
          emptyDescription="Chat activity will appear here as your team uses the AI."
        />
        <div className="aichat-analytics__footnote">
          Click a request to see its prompt and response. Showing the most recent {REQUEST_FETCH_SIZE} requests in the
          selected period. Free Ask-mode conversations aren't billed and don't appear here.
        </div>
      </div>
    );
  }

  // ── Render ───────────────────────────────────────────────────────

  render() {
    const { range, analytics, analyticsLoading, analyticsError, selectedRequest } = this.state;

    if (analyticsError && analytics === null) {
      return (
        <div className="aichat-analytics">
          <div className="aichat-analytics__error">
            <KuikaSvg name="info-circle" width={24} height={24} />
            <div>
              <div className="aichat-analytics__error-title">We couldn't load your analytics</div>
              <div className="aichat-analytics__error-body">{analyticsError}</div>
            </div>
            <KuikaButton type="secondary" size="sm" onClick={() => this.loadAll(range)}>
              Retry
            </KuikaButton>
          </div>
        </div>
      );
    }

    if (analyticsLoading && analytics === null) {
      // The page's shape — the KPI band and the request log — instead of a full-screen spinner over
      // the whole designer (L7); the trend chart keeps its own placeholder once the page is up.
      return (
        <div className="aichat-analytics">
          <KuikaSkeletonRegion label="Loading analytics">
            <div className="aichat-analytics__skeleton">
              <KuikaSkeletonStats count={4} />
              <KuikaSkeletonTable rows={5} header />
            </div>
          </KuikaSkeletonRegion>
        </div>
      );
    }

    return (
      <div className={`aichat-analytics${analyticsLoading ? " aichat-analytics--refreshing" : ""}`}>
        <div className="aichat-analytics__controls">
          <KuikaTabs
            size="sm"
            options={RANGE_OPTIONS}
            value={range}
            onChange={this.handleRangeChange}
            dataTestId="aiChatbotAnalyticsRange"
          />
        </div>

        {this.renderKpis()}
        {this.renderTrendCard()}
        {this.renderBreakdownCard()}
        {this.renderRequestsCard()}

        {selectedRequest && (
          <AnalyticsRequestDrawer request={selectedRequest} onClose={() => this.setState({ selectedRequest: null })} />
        )}
      </div>
    );
  }
}

export default AnalyticsTab;
